//////////////////////////
//
// @todo rename this file to store.helpers when all reducers are using MappedEntityState2
import { Dispatch } from 'redux';
import { assoc, omit, filter, pathOr, values, head, propEq, find, set, lensPath, flatten } from 'ramda';
import {
  Entity,
  EntityError,
  EntityMap,
  MappedEntityState2 as MappedEntityState,
  ThunkActionCreator,
} from 'src/store/types';
import { AsyncActionCreators } from 'typescript-fsa';
import { ApiFieldError } from 'src/types';
import { isNotEmpty } from 'src/utilities/hasValue';
import {
  filterResourcesByOwnerReferenceId,
  getAccountId,
} from 'src/services/objects/finder';
import { authenticatedUser, currentAppSession, primaryApplicantContext } from 'src/utilities/storage';
import {
  getResourceById,
  getTheResourceByAccountId,
} from 'src/utilities/borrowers';
import {
  REDUX_APPLICANT_ACCOUNTS,
  ITEMS_BY_ID,
  USER_REDUX_RESOURCE_NAME,
} from './constants';
import { ApplicationState } from './ApplicationState';

type APIError = ApiFieldError;

export const addEntityRecord = <T extends Entity>(
  result: EntityMap<T>,
  current: T,
): EntityMap<T> => assoc(String(current.id), current, result);

export const onStart = <S>(state: S) =>
  Object.assign({}, state, { loading: true, error: { read: undefined } });

export const onGetAllSuccess = <E extends Entity, S>(
  items: E[],
  state: any,
  results: any,
  update: (e: E) => E = (i) => i,
): S =>
  Object.assign({}, state, {
    loading: false,
    lastUpdated: Date.now(),
    results,
    itemsById: items.reduce(
      (itemsById, item) => ({ ...itemsById, [item.id]: update(item) }),
      {},
    ),
  });

export const setError = <E extends Entity>(
  error: EntityError,
  state: MappedEntityState<E, EntityError>,
) => {
  return Object.assign({}, state, { error: { ...state.error, ...error } });
};

export const onError = <S = {}, E = APIError[] | undefined>(
  error: E,
  state: S,
) => Object.assign({}, state, { error, loading: false });

export const createDefaultState = <E extends Entity, O extends EntityError>(
  override: Partial<MappedEntityState<E, O>> = {},
  defaultError: O = {} as O,
): MappedEntityState<E, O> => ({
  itemsById: {},
  loading: false,
  lastUpdated: 0,
  error: defaultError as O, // @todo decide on better approach to error typing
  results: 0,
  ...override,
});

export const onDeleteSuccess = <E extends Entity, O = APIError[] | undefined>(
  id: any | string,
  state: MappedEntityState<E, O>,
): MappedEntityState<E, O> => {
  return removeMany([String(id)], state);
};

export const onCreateOrUpdate = <E extends Entity, O = APIError[] | undefined>(
  entity: E,
  state: MappedEntityState<E, O>,
): MappedEntityState<E, O> => {
  return addMany(flatten([entity]) as any, state);
};

export const removeMany = <E extends Entity, O = APIError[] | undefined>(
  list: string[],
  state: MappedEntityState<E, O>,
): MappedEntityState<E, O> => {
  const itemsById = omit(list, state.itemsById);

  return {
    ...state,
    itemsById,
    loading: false,
    results: Object.keys(itemsById).length,
  };
};

const addMany = <E extends Entity, O = APIError[] | undefined>(
  list: E[],
  state: MappedEntityState<E, O>,
  results?: any,
): MappedEntityState<E, O> => {
  /**
   Incase 404 response get on API call we consider that call as success
   And its response will be 'undefined' for that case if we call this `addMany`
   this item.id will throw error so to avoid that we using filter here;

  */
  const itemsById = filter(
    (a: any) => isNotEmpty(pathOr('', ['id'], a)),
    list,
  ).reduce(
    (previousItems, item) => ({ ...previousItems, [item.id]: item }),
    state.itemsById,
  );

  return {
    ...state,
    itemsById,
    loading: false,
    results: state.results,
  };
};

/**
 * Generates a list of entities added to an existing list, and a list of entities removed from an existing list.
 */
export const getAddRemoved = <E extends Entity>(
  existingList: E[] = [],
  newList: E[] = [],
) => {
  const existingIds = existingList.map(({ id }) => String(id));
  const newIds = newList.map(({ id }) => String(id));

  const added = newList.filter(({ id }) => !existingIds.includes(String(id)));

  const removed = existingList.filter(({ id }) => !newIds.includes(String(id)));

  return [added, removed];
};

export const createRequestThunk = <Req, Res, Err>(
  actions: AsyncActionCreators<Req, Res, Err>,
  request: (params: Req) => Promise<any>,
): ThunkActionCreator<Promise<Res>, Req> => {
  return (params: Req) => async (dispatch) => {
    const { started, done, failed } = actions;
    dispatch(started(params));

    try {
      const result = await request(params);
      const doneAction = done({ result, params });
      dispatch(doneAction);
      return result;
    } catch (error) {
      const failAction = failed({ error, params });
      dispatch(failAction);
      return Promise.reject(error);
    }
  };
};

export const updateInPlace = <E extends Entity>(
  id: number | string,
  update: (e: E) => E,
  state: MappedEntityState<E>,
) => {
  const { itemsById } = state;

  // If this entity cannot be found in state, return the state as-is.
  if (!itemsById[id]) {
    return state;
  }

  // Return the state as-is EXCEPT replacing the original entity with the updated entity.
  const updated = update(itemsById[id]);
  return {
    ...state,
    itemsById: {
      ...itemsById,
      [id]: updated,
    },
  };
};

// Given a nested state and an ID, ensures that MappedEntityState exists at the
// provided key. If the nested state already exists, return the state untouched.
// If it doesn't exist, initialize the state with `createDefaultState()`.
export const ensureInitializedNestedState = (
  state: Record<number, any>,
  id: number,
  override: any = {},
) => {
  if (!state[id]) {
    state[id] = createDefaultState({ ...override, error: {} });
  }
  return state;
};

export const apiResponseToMappedState = <T extends Entity>(data: T[]) => {
  return data.reduce((acc, thisEntity) => {
    return set(lensPath([thisEntity.id]),thisEntity, acc) ;
  }, {});
};

/////////////////////////////////////////////////////////////////////////////
//

/* Dispatch any action to the redux store from external functions
 * This isn't thread safe, which means runs concurrently and must be avoided
 * But there is not choice but to use in the BotServices we have.
 **/
export const onServiceDispatchAction = (
  dispatch: Dispatch<any>,
  serviceAction: (dispatch: Dispatch<any>) => void,
) => {
  return dispatch(serviceAction);
};

export const getAccountByLoanRole = (loanApp: any, loanRoleId: string) => {
  const app_accounts = pathOr([], ['spec', 'user_accounts'], loanApp);
  const loanRoles = pathOr([], ['spec', 'loanapp_roles'], loanApp);
  const activeRole = getResourceById(loanRoles, loanRoleId);
  return getResourceById(app_accounts, getAccountId(activeRole));
};

export const getAccountByActiveLoanRole = (state: any) => {
  const { loanRoleId } = currentAppSession();
  const loanApp = pathOr(
    {},
    [USER_REDUX_RESOURCE_NAME, 'loanApp', 'data'],
    state,
  );
  return loanRoleId
    ? getAccountByLoanRole(loanApp, loanRoleId)
    : head(pathOr([], ['spec', 'user_accounts'], loanApp));
};

export const getItemsByIdResources = (
  resourceName: string,
  state: ApplicationState,
) => {
  return values(
    pathOr({}, [USER_REDUX_RESOURCE_NAME, resourceName, ITEMS_BY_ID], state),
  );
};

export const getItemsResourcesData = (
  resourceName: string,
  state: ApplicationState,
) => {
  return pathOr({}, [USER_REDUX_RESOURCE_NAME, resourceName, 'data'], state);
};

export const getItemsByIdResourcesWithActiveLoanRole = (
  resourceName: string,
  state: any,
) => {
  const { loanRoleId } = currentAppSession();
  const resources = values(
    pathOr({}, [USER_REDUX_RESOURCE_NAME, resourceName, ITEMS_BY_ID], state),
  );

  return loanRoleId
    ? filterResourcesByOwnerReferenceId(resources, loanRoleId)
    : resources;
};

export const getItemsByIdResourcesWithActiveLoanId = (
  resourceName: string,
  state: any,
) => {
  const { loanAppId } = currentAppSession();
  const resources = values(
    pathOr({}, [USER_REDUX_RESOURCE_NAME, resourceName, ITEMS_BY_ID], state),
  );

  return loanAppId
    ? filterResourcesByOwnerReferenceId(resources, loanAppId)
    : resources;
};

export const getResourcesOfAuthenticatedUser = (
  resourceName: string,
  state: ApplicationState,
) => {
  const { accountId } = primaryApplicantContext();
  const resources = values(
    pathOr({}, [USER_REDUX_RESOURCE_NAME, resourceName, ITEMS_BY_ID], state),
  );
  return getTheResourceByAccountId(resources, accountId);
};

export const getAuthenticatedAccount = (items: any) => {
  const email = authenticatedUser.accountEmail.get();
  return find(propEq('email', email), items);
};

export const getActiveBorrowerAccount = (state: any) => {
  const { loanRoleAccountId } = currentAppSession();
  return pathOr(
    {},
    [
      USER_REDUX_RESOURCE_NAME,
      REDUX_APPLICANT_ACCOUNTS,
      ITEMS_BY_ID,
      loanRoleAccountId,
    ],
    state,
  );
};
